import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, OnDestroy, inject, Injector, ChangeDetectorRef } from '@angular/core';
import { Router } from "@angular/router";
import { FormControl, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';

import { AgGridModule } from 'ag-grid-angular';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { ContractService } from '../../service/entity-services/contract.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { PermissionsService } from '../../service/permissions/permissions.service';

import { ContractDetailCellRendererComponent } from './contract-detail-cell-renderer/contract-detail-cell-renderer.component';
import { NewWorkflowRequestButtonComponent } from '../../shared/components/new-workflow-request-button/new-workflow-request-button.component';
import { EntityList } from '../../objects/entity-list';
import { Contract } from '../../objects/contract';
import { environment } from '../../../environments/environment';

import * as contractColumns from '../../../app/shared/entity-columns/contract-columns';
import { setContractColumnsInjector } from '../../../app/shared/entity-columns/contract-columns';
import * as grantsColumns from '../../../app/shared/entity-columns/columns-contract-grants';
import * as acuteCareFinancialColumns from '../../../app/shared/entity-columns/contract-acute-financial';
import * as valueColumns from '../../../app/shared/entity-columns/contract-value';
import * as hrColumns from '../../../app/shared/entity-columns/contract-hr';
import * as columnsContractExtensions from '../../../app/shared/entity-columns/contract-extensions';
import * as apColumns from '../../../app/shared/entity-columns/contract-ap';
import * as sochsColumns from '../../../app/shared/entity-columns/contract-sochs-accordion';
import * as salaryAndBenefitsColumns from '../../../app/shared/entity-columns/contract-columns-salary-and-benefits';

@Component({
  selector: 'app-contracts',
  templateUrl: './contracts.component.html',
  styleUrls: ['./contracts.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatGridListModule,
    MatDividerModule,
    MatSidenavModule,
    MatProgressSpinner,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    NewWorkflowRequestButtonComponent
  ]
})
export class ContractsComponent extends EntityList implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private sidenavContentService = inject(SidenavContentService);
  private permissionsService = inject(PermissionsService);
  private injector = inject(Injector);
  private cdr = inject(ChangeDetectorRef);
  // contractService is inherited from Entity base class

  @ViewChild('contractsSidenavActions') contractsSidenavActions!: TemplateRef<any>;

  // Observable for contracts visible to the user
  visibleContracts$: Observable<Contract[]> = this.contractService.visibleContracts$;

  // UI state
  columnChooserControl = new FormControl();
  customizeColumnsUi = false;
  waitingForServer = false;
  showNameSavedView = false;
  userRole: string | null = null;
  isInitializing = true; // New flag for loading screen

  // Column configuration
  defaultCols: any[] = [
    contractColumns.colContractId,
    contractColumns.colContractStatus,
    contractColumns.colContractName,
    contractColumns.colContractType,
    contractColumns.colContractPartyFromContractData,
    contractColumns.colContractExpirationDate,
    contractColumns.colContractRenewalType
  ];

  selectedCols = this.defaultCols;

  // Available columns based on environment configuration
  availableColumns: any[] = [];
  availableColumnSelectList: {value: any, viewValue: string}[] = [];

  // Column selector for customize columns dialog
  columnSelectorControl = new FormControl<string>('');
  selectedColumnsList: any[] = [];
  filteredColumns$!: Observable<{value: any, viewValue: string}[]>;

  // AG Grid configuration
  public frameworkComponents;
  public detailCellRenderer;

  // Track the currently expanded detail row node
  private currentlyExpandedNode: any = null;

  // Default column definition - applies to all columns
  public defaultColDef = {
    filter: true, // Enable filtering on all columns
    floatingFilter: false, // Hide floating filter row by default (ugly and bulky)
    resizable: true, // Allow column resizing
    sortable: true, // Enable sorting
    menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'], // Add filter to column menu
  };

  // Toggle for showing/hiding floating filters
  public showFloatingFilters = false;

  constructor() {
    super();
    this.entityType = 'contracts'; // Set the entity type for view management
    this.detailCellRenderer = 'myDetailCellRenderer';
    this.frameworkComponents = { myDetailCellRenderer: ContractDetailCellRendererComponent };

    // Set the injector for contract column formatters to access services
    setContractColumnsInjector(this.injector);

    // Build available columns based on environment flags
    this.buildAvailableColumns();

    // Initialize session storage (this will restore selectedCols if session exists)
    this.initializeSessionStorage();
  }

  /**
   * Builds the list of available columns based on environment configuration
   */
  private buildAvailableColumns(): void {
    // Start with base contract columns
    this.availableColumns = [...contractColumns.allColumns];

    // Add conditional columns based on environment flags
    if (environment.grants) {
      this.availableColumns = [...this.availableColumns, ...Object.values(grantsColumns)];
    }

    if (environment.value) {
      this.availableColumns = [...this.availableColumns, ...Object.values(valueColumns)];
    }

    if (environment.hrAccordion) {
      this.availableColumns = [...this.availableColumns, ...Object.values(hrColumns)];
    }

    if (environment.acuteCareFinancial) {
      this.availableColumns = [...this.availableColumns, ...Object.values(acuteCareFinancialColumns)];
    }

    if (environment.sochsAccordion) {
      this.availableColumns = [...this.availableColumns, ...Object.values(sochsColumns)];
    }

    // TODO: Map physicianCompensation to apColumns when correlation is confirmed
    // if (environment.physicianCompensation) {
    //   this.availableColumns = [...this.availableColumns, ...Object.values(apColumns)];
    // }

    // Add salary and benefits columns if physician compensation is enabled
    if (environment.physicianCompensation) {
      this.availableColumns = [...this.availableColumns, ...Object.values(salaryAndBenefitsColumns)];
    }

    // TODO: Determine if contract extensions have an environment flag
    // For now, assuming they're always available
    this.availableColumns = [...this.availableColumns, ...Object.values(columnsContractExtensions)];

    // Filter out any non-column objects and build select list
    this.availableColumns = this.availableColumns.filter(col => col && col.colId && col.headerName);

    // Remove duplicates by colId
    const uniqueColsMap = new Map();
    this.availableColumns.forEach(col => {
      if (!uniqueColsMap.has(col.colId)) {
        uniqueColsMap.set(col.colId, col);
      }
    });
    this.availableColumns = Array.from(uniqueColsMap.values());

    // Build select list for autocomplete
    this.availableColumnSelectList = this.availableColumns.map(col => ({
      value: col,
      viewValue: col.headerName
    }));

    // Sort by header name for easier selection
    this.availableColumnSelectList.sort((a, b) => a.viewValue.localeCompare(b.viewValue));
  }

  async ngOnInit(): Promise<void> {
    // Service will automatically load contracts
    // NOTE: View loading is now deferred to after grid is ready (see completeInitialization)

    // Set up autocomplete filtering for column selector
    this.filteredColumns$ = this.columnSelectorControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterColumns(value || ''))
    );
  }

  /**
   * Completes the grid initialization after grid is ready.
   * Uses official AG Grid APIs for state management.
   * Priority: Session Storage > Saved Views > Auto-size
   */
  private async completeInitialization(): Promise<void> {
    // CRITICAL: Suspend session tracking during initialization
    // This prevents premature state capture during view loading
    this.suspendSessionTracking = true;

    // PRIORITY 1: Check for session storage (temporary column adjustments)
    const hasSessionState = this.sessionStorageKey &&
      sessionStorage.getItem(this.sessionStorageKey);

    if (hasSessionState) {
      this.loadSessionState();
      // Re-enable session tracking after session state is loaded
      this.suspendSessionTracking = false;
    } else {
      // PRIORITY 2: Load saved views from backend
      await this.loadUserViews();

      // Check if default view uses new gridState format (preferred) or old format
      const hasNewFormatView = this.savedNamedViews && (this.savedNamedViews as any).gridState;
      const hasOldFormatView = this.savedNamedViews &&
        ((this.savedNamedViews as any).columnState || (this.savedNamedViews as any).savedViewData);

      if (hasNewFormatView || hasOldFormatView) {
        // Load the default saved view from backend
        await this.loadNamedView(this.savedNamedViews);

        // Re-enable session tracking after view is fully loaded
        this.suspendSessionTracking = false;
      } else {
        // PRIORITY 3: No session, no saved view - auto-size
        this.autoSizeAll();
        // autoSizeAll() has its own 1000ms timeout that will re-enable tracking
        // Wait for that to complete (1000ms + a bit of buffer)
        await new Promise(resolve => setTimeout(resolve, 1100));
      }
    }

    // Hide loading screen - must trigger change detection since we're outside Angular's zone
    this.isInitializing = false;
    this.cdr.detectChanges(); // Manually trigger change detection
  }

  /**
   * Filters available columns based on user input
   */
  private _filterColumns(value: string | any): {value: any, viewValue: string}[] {
    // Handle both string input and object values (when an option is selected)
    if (!value || typeof value !== 'string') {
      return this.availableColumnSelectList;
    }
    const filterValue = value.toLowerCase();
    return this.availableColumnSelectList.filter(option =>
      option.viewValue.toLowerCase().includes(filterValue)
    );
  }

  /**
   * Display function for autocomplete - returns empty string to keep input clear
   */
  displayColumnFn(): string {
    return '';
  }

  /**
   * Override onGridReady to add single-expand-at-a-time functionality for detail rows
   * and to complete initialization after grid is ready
   */
  override onGridReady(params: any): void {
    // Call parent class onGridReady first
    super.onGridReady(params);

    // Add listener to ensure only one detail row is expanded at a time
    this.gridApi.addEventListener('expandedChanged', (event: any) => {
      const node = event.node;

      // Only handle expand events (not collapse)
      if (node && node.expanded && node.master) {
        // If there's a currently expanded node that's different from this one
        if (this.currentlyExpandedNode && this.currentlyExpandedNode !== node) {
          // Collapse the previously expanded node
          this.currentlyExpandedNode.setExpanded(false);
        }

        // Track the newly expanded node
        this.currentlyExpandedNode = node;
      }

      // If this node is being collapsed and it was the tracked one, clear tracking
      if (node && !node.expanded && this.currentlyExpandedNode === node) {
        this.currentlyExpandedNode = null;
      }
    });

    // Complete initialization now that grid is ready
    // This loads views and sets up the grid behind the loading screen
    // Call async method but don't await (can't await in non-async callback)
    this.completeInitialization().catch(err => {
      console.error('[onGridReady] Error during initialization:', err);
      this.isInitializing = false; // Hide loading screen on error
    });
  }

  ngAfterViewInit(): void {
    this.sidenavContentService.updateSidenavContent(this.contractsSidenavActions);
  }

  ngOnDestroy(): void {
    this.sidenavContentService.updateSidenavContent(null);
  }

  // Navigation
  goToArchivedContracts(): void {
    this.router.navigateByUrl('/securehome/archived-contracts');
  }

  createNewContract(): void {
    this.router.navigateByUrl('/securehome/new-contract');
  }

  // Grid events
  onCellClicked(event: any): void {
    console.log('Contract clicked:', event.data);
    this.router.navigate(['/securehome/contract', event.data.contractNum]);
  }

  onSearchChange(searchText: string): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', searchText);
    }
  }

  // View management methods (saveView, saveNamedViewNow, loadNamedView,
  // openSavedReportsDialog, openDeleteSavedViewsDialog) are inherited from EntityList base class

  // Column management methods
  /**
   * Adds a column to the selected columns list
   */
  selectColumn(column: any): void {
    if (column && !this.selectedColumnsList.find(c => c.colId === column.colId)) {
      this.selectedColumnsList.push(column);
      // Clear the input after selection using setTimeout to avoid race condition with Material's autocomplete
      setTimeout(() => {
        this.columnSelectorControl.setValue('', { emitEvent: false });
      }, 0);
    }
  }

  /**
   * Removes a column from the selected columns list
   */
  removeColumn(column: any): void {
    const index = this.selectedColumnsList.findIndex(c => c.colId === column.colId);
    if (index >= 0) {
      this.selectedColumnsList.splice(index, 1);
    }
  }

  /**
   * Applies the selected columns to the grid
   */
  applyColumns(): void {
    if (this.gridApi && this.selectedColumnsList.length > 0) {
      this.selectedCols = this.selectedColumnsList;
      this.gridApi.setGridOption('columnDefs', this.selectedCols);
      this.customizeColumnsUi = false;

      // Mark as having unsaved changes
      this.markSessionAsModified();
    }
  }

  /**
   * Opens the customize columns dialog
   */
  openCustomizeColumnsDialog(): void {
    // Initialize selected columns list with currently visible columns
    if (this.gridApi) {
      const currentState = this.gridApi.getColumnState();
      const visibleColIds = currentState
        .filter((col: any) => !col.hide)
        .map((col: any) => col.colId);

      this.selectedColumnsList = this.availableColumns.filter(col =>
        visibleColIds.includes(col.colId)
      );
    } else {
      this.selectedColumnsList = [...this.selectedCols];
    }

    this.customizeColumnsUi = true;
  }

  /**
   * Toggles the floating filter row on/off
   */
  toggleFloatingFilters(): void {
    this.showFloatingFilters = !this.showFloatingFilters;

    if (this.gridApi) {
      // Update all columns to show/hide floating filters
      const allColumnIds: string[] = [];
      this.gridApi.getColumns()?.forEach((column: any) => {
        allColumnIds.push(column.getId());
      });

      allColumnIds.forEach(colId => {
        this.gridApi.setColumnFilterProperty(colId, 'floatingFilter', this.showFloatingFilters);
      });

      // Force grid to refresh to apply changes
      this.gridApi.refreshHeader();
    }
  }

  // Export functions with date formatting
  exportCSV(): void {
    if (this.gridApi) {
      const params = {
        processCellCallback: this.formattingFunction,
        fileName: 'contracts_export.csv'
      };
      this.gridApi.exportDataAsCsv(params);
    }
  }

  exportExcel(): void {
    if (this.gridApi) {
      const excelParams = {
        processCellCallback: this.formattingFunction,
        fileName: 'contracts_export.xlsx'
      };
      this.gridApi.exportDataAsExcel(excelParams);
    }
  }
}